// ════════════════════════════════════════════════════════════
// usePreventClose — Garde-fou async + animation shake
// ════════════════════════════════════════════════════════════
// Wrap un `onOpenChange` Radix/Vaul en :
//   1. Si `preventClose` est défini ET retourne false (ou Promise<false>),
//      la fermeture est annulée + animation shake déclenchée sur le content.
//   2. Sinon, la fermeture se propage normalement.
//
// L'animation shake est gérée via Framer Motion `useAnimationControls`.
// Respecte `prefers-reduced-motion` (pas de translate, juste pulse opacity).
// ════════════════════════════════════════════════════════════
import * as React from 'react';
import { useAnimationControls } from 'framer-motion';

export type PreventCloseFn = () => boolean | Promise<boolean>;

interface UsePreventCloseOptions {
  preventClose?: PreventCloseFn;
  onOpenChange?: (open: boolean) => void;
}

interface UsePreventCloseResult {
  handleOpenChange: (open: boolean) => void;
  /**
   * Évalue preventClose et propage la fermeture si autorisée. À appeler
   * depuis les handlers DOM (onEscapeKeyDown, onPointerDownOutside) APRÈS
   * un e.preventDefault() — garantit que la primitive sous-jacente
   * (Radix/Vaul) ne ferme jamais avant qu'on ait évalué le garde-fou.
   */
  requestClose: () => void;
  /** Contrôles Framer Motion à attacher au content : `<motion.div animate={shakeControls}>`. */
  shakeControls: ReturnType<typeof useAnimationControls>;
  /** Déclenche le shake manuellement (utile pour les handlers DOM). */
  runShake: () => void;
  /** True pendant qu'une vérification async `preventClose` est en cours. */
  isChecking: boolean;
  /** Reflet de la prop preventClose (true si non-null). */
  hasPreventClose: boolean;
}

const SHAKE_KEYFRAMES = [0, -4, 4, -4, 4, 0];
const SHAKE_DURATION = 0.15;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function usePreventClose({
  preventClose,
  onOpenChange,
}: UsePreventCloseOptions): UsePreventCloseResult {
  const shakeControls = useAnimationControls();
  const [isChecking, setIsChecking] = React.useState(false);

  const runShake = React.useCallback(() => {
    if (prefersReducedMotion()) {
      // Pulse opacity en remplacement (perceptible sans mouvement)
      void shakeControls.start({
        opacity: [1, 0.7, 1],
        transition: { duration: 0.2 },
      });
      return;
    }
    void shakeControls.start({
      x: SHAKE_KEYFRAMES,
      transition: { duration: SHAKE_DURATION, ease: 'easeInOut' },
    });
  }, [shakeControls]);

  const evaluateClose = React.useCallback(() => {
    if (!preventClose) {
      onOpenChange?.(false);
      return;
    }
    setIsChecking(true);
    const result = preventClose();
    void Promise.resolve(result)
      .then((allow) => {
        if (allow) {
          onOpenChange?.(false);
        } else {
          runShake();
        }
      })
      .finally(() => {
        setIsChecking(false);
      });
  }, [preventClose, onOpenChange, runShake]);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      // Ouverture : pas de garde-fou. On défocus l'active element
      // d'abord pour éviter le warning Chrome « aria-hidden on focused
      // descendant » (Radix marque aria-hidden sur les siblings avant
      // de bouger le focus dans la modale).
      if (open) {
        if (
          typeof document !== 'undefined' &&
          document.activeElement instanceof HTMLElement &&
          document.activeElement !== document.body
        ) {
          document.activeElement.blur();
        }
        onOpenChange?.(true);
        return;
      }
      evaluateClose();
    },
    [evaluateClose, onOpenChange],
  );

  return {
    handleOpenChange,
    requestClose: evaluateClose,
    shakeControls,
    runShake,
    isChecking,
    hasPreventClose: Boolean(preventClose),
  };
}
